import { tr, usePartnerTranslation } from "../lib/language";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { parseApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const QUERY_TYPES = [
  "Account access",
  "Bookings",
  "Product setup",
  "Legal center",
  "Payments",
  "Technical issue",
  "Other",
];

const initialForm = {
  name: "",
  email: "",
  queryType: "",
  message: "",
  attachment: null,
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MESSAGE_MAX_LENGTH = 2000;
const ALLOWED_ATTACHMENT_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
]);
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;
const ATTACHMENT_ACCEPT = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";

const formatFileSize = (bytes) => `${(bytes / (1024 * 1024)).toFixed(0)} MB`;

export default function ContactPage() {
  usePartnerTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [fileInputKey, setFileInputKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [successInquiry, setSuccessInquiry] = useState(null);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  };

  useEffect(() => {
    if (user) navigate("/support", { replace: true });
  }, [navigate, user]);

  const validateAttachment = (file) => {
    if (!file) return "";
    if (!ALLOWED_ATTACHMENT_TYPES.has(file.type)) {
      return tr("Only PDF, JPG, JPEG, and PNG files are supported.");
    }
    if (file.size > MAX_ATTACHMENT_SIZE) {
      return `Attachment must be ${formatFileSize(MAX_ATTACHMENT_SIZE)} or less.`;
    }
    return "";
  };

  const handleAttachmentChange = (event) => {
    const file = event.target.files?.[0] || null;
    const error = validateAttachment(file);
    if (error) {
      setField("attachment", null);
      setErrors((prev) => ({ ...prev, attachment: error }));
      setFileInputKey((key) => key + 1);
      toast.error(error);
      return;
    }
    setField("attachment", file);
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Enter your name.";
    if (!EMAIL_REGEX.test(form.email.trim())) next.email = "Enter a valid email address.";
    if (!form.queryType) next.queryType = "Select a query type.";
    if (!form.message.trim()) next.message = tr("Enter your message.");
    if (form.message.trim().length > MESSAGE_MAX_LENGTH) {
      next.message = `Message must be ${MESSAGE_MAX_LENGTH} characters or less.`;
    }
    const attachmentError = validateAttachment(form.attachment);
    if (attachmentError) next.attachment = attachmentError;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      toast.error(tr("Please fix the highlighted fields."));
      return;
    }

    const body = new FormData();
    body.append("name", form.name.trim());
    body.append("email", form.email.trim());
    body.append("query_type", form.queryType);
    body.append("message", form.message.trim());
    if (form.attachment) body.append("attachment", form.attachment);

    setSubmitting(true);
    try {
      const { data } = await api.post("/contact-inquiries", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccessInquiry(data);
      setForm(initialForm);
      setFileInputKey((key) => key + 1);
    } catch (err) {
      const parsed = parseApiError(err, tr("We couldn't send your message. Please try again."));
      const apiErrors = parsed.errors || {};
      setErrors({
        name: apiErrors.name,
        email: apiErrors.email,
        queryType: apiErrors.query_type,
        message: apiErrors.message,
        attachment: apiErrors.attachment,
      });
      toast.error(parsed.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex-1 px-4 py-10 sm:px-6 lg:px-10">
      <div className="mx-auto w-full max-w-[760px]">
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-[#0F1115] sm:text-4xl">{tr("Contact us")}</h1>
        </header>

        <form
          onSubmit={handleSubmit}
          className="rounded-lg border border-[#E5E7EB] bg-white p-5 shadow-sm sm:p-6"
          noValidate
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm font-semibold text-[#0F1115]">{tr("Name *")}</span>
              <Input
                value={form.name}
                onChange={(event) => setField("name", event.target.value)}
                className={`mt-2 h-11 ${errors.name ? "border-[#D92D20]" : "border-[#D9DDE3]"}`}
                placeholder={tr("Your name")}
              />
              {errors.name && <p className="mt-1 text-xs text-[#D92D20]">{errors.name}</p>}
            </label>

            <label className="block">
              <span className="text-sm font-semibold text-[#0F1115]">{tr("Email *")}</span>
              <Input
                type="email"
                value={form.email}
                onChange={(event) => setField("email", event.target.value)}
                className={`mt-2 h-11 ${errors.email ? "border-[#D92D20]" : "border-[#D9DDE3]"}`}
                placeholder="you@example.com"
              />
              {errors.email && <p className="mt-1 text-xs text-[#D92D20]">{errors.email}</p>}
            </label>
          </div>

          <div className="mt-5">
            <div>
              <span className="text-sm font-semibold text-[#0F1115]">{tr("Query type *")}</span>
              <Select
                value={form.queryType}
                onValueChange={(value) => setField("queryType", value)}
              >
                <SelectTrigger
                  className={`mt-2 h-11 border bg-white ${errors.queryType ? "border-[#D92D20]" : "border-[#D9DDE3]"}`}
                >
                  <SelectValue placeholder={tr("Select a topic")} />
                </SelectTrigger>
                <SelectContent>
                  {QUERY_TYPES.map((type) => (
                    <SelectItem key={tr(type)} value={tr(type)}>
                      {tr(type)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.queryType && (
                <p className="mt-1 text-xs text-[#D92D20]">{errors.queryType}</p>
              )}
            </div>
          </div>

          <label className="mt-5 block">
            <span className="text-sm font-semibold text-[#0F1115]">{tr("Message *")}</span>
            <Textarea
              value={form.message}
              maxLength={MESSAGE_MAX_LENGTH}
              onChange={(event) => setField("message", event.target.value)}
              className={`mt-2 min-h-36 resize-y ${errors.message ? "border-[#D92D20]" : "border-[#D9DDE3]"}`}
              placeholder={tr("How can we help?")}
            />
            <div className="mt-1 flex items-center justify-between gap-3">
              {errors.message ? (
                <p className="text-xs text-[#D92D20]">{errors.message}</p>
              ) : (
                <span />
              )}
              <p className="shrink-0 text-xs text-[#8A8F98]">
                {form.message.length}/{MESSAGE_MAX_LENGTH}
              </p>
            </div>
          </label>

          <div className="mt-5">
            <label className="block">
              <span className="text-sm font-semibold text-[#0F1115]">{tr("Attachment ")}<span className="font-normal text-[#8A8F98]">{tr("(optional)")}</span>
              </span>
              <Input
                key={fileInputKey}
                id="contact-attachment"
                type="file"
                accept={ATTACHMENT_ACCEPT}
                onChange={handleAttachmentChange}
                className="sr-only"
              />
              <span
                className={`mt-2 flex min-h-11 items-center justify-between gap-3 rounded-md border bg-white px-3 py-2 text-sm ${errors.attachment ? "border-[#D92D20]" : "border-[#D9DDE3]"}`}
              >
                <span className="truncate text-[#5b5f66]">
                  {form.attachment ? form.attachment.name : tr("No file selected")}
                </span>
                <span className="shrink-0 rounded-md bg-[#F3F4F6] px-3 py-1.5 font-semibold text-[#0F1115]">{tr("Upload file")}</span>
              </span>
            </label>
            <p className="mt-1 text-xs text-[#8A8F98]">
              PDF, JPG, JPEG, or PNG only. Max 10 MB.
            </p>
            {errors.attachment && (
              <p className="mt-1 text-xs text-[#D92D20]">{errors.attachment}</p>
            )}
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="humrahe-btn-primary inline-flex h-11 items-center justify-center rounded-lg px-6 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? tr("Sending...") : tr("Send message")}
            </button>
          </div>
        </form>
      </div>
      <Dialog open={!!successInquiry} onOpenChange={(open) => !open && setSuccessInquiry(null)}>
        <DialogContent className="max-w-md rounded-lg border border-[#E5E7EB] bg-white p-0">
          <DialogHeader className="border-b border-[#EFE4E4] px-6 py-5">
            <DialogTitle className="text-lg font-extrabold tracking-tight text-[#0F1115]">{tr("Message received")}</DialogTitle>
            <DialogDescription className="text-sm text-[#6B7280]">{tr("Thanks for reaching out. Your message has been sent to the Voocly team.")}</DialogDescription>
          </DialogHeader>
          <div className="px-6 py-5">
            <p className="text-sm text-[#5b5f66]">{tr("We will review your query and reply to your email address. Keep this reference for follow-up:")}</p>
            <p className="mt-3 rounded-md bg-[#F9FAFB] px-3 py-2 text-sm font-semibold text-[#0F1115]">
              {successInquiry?.reference}
            </p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setSuccessInquiry(null)}
                className="humrahe-btn-primary inline-flex h-10 items-center justify-center rounded-lg px-5 text-sm font-semibold"
              >{tr("Done")}</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}
